import { cn } from '@/lib/utils';
import { isEmpty } from '@chiroup/core/functions/isEmpty';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { Macro } from '@chiroup/core/types/Macro';
import { DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { ChangeEvent, ReactNode, useCallback, useImperativeHandle, useState } from 'react';
import { Loading } from '../Loading';
import { TrivialTooltip } from '../TrivialTooltip';
import { ErrorIcon } from './ErrorIcon';
import { FieldErrors } from './FieldErrors';
import MacrosPopover from './MacrosPopover';
import useMacros from './useMacros';

type InputRef = {
  focus: () => void;
  blur: () => void;
  // Add any other methods or properties you want to expose
};

type Props = {
  name: string;
  className?: string;
  icon?: ReactNode;
  label?: string | ReactNode;
  labelClassName?: string;
  value?: string | number | null;
  onChange?: (val: any) => void;
  onBlur?: (val: string) => void;
  errors?: FormError;
  disabled?: boolean;
  onFocus?: () => void;
  type?: string;
  hint?: string | React.ReactNode;
  hintOnClick?: () => void;
  autoComplete?: string;
  inputClassName?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  step?: string;
  clickIcon?: boolean;
  autoFocus?: boolean;
  inputMode?: 'text' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  pattern?: string;
  loading?: boolean;
  inputStyle?: { [key: string]: string | number };
  maxLength?: number;
  iconRight?: boolean;
  onEnter?: () => void;
  macros?: Partial<Macro>[];
  downError?: string;
  initialValue?: number;
  tooltip?: string;
  tooltipId?: string;
  inputTitle?: string;
  labelTitle?: string;
  /**
   * Optional prefix to display before the input field
   */
  prefix?: string | React.ReactNode;
  /**
   * Optional className for the prefix
   */
  prefixClassName?: string;
  /**
   * Whether to show a copy button in the upper right corner
   */
  showCopyButton?: boolean;
  /**
   * Optional callback when copy is successful
   */
  onCopySuccess?: () => void;
  /**
   * The 'inline' is special-purpose for use by HC. It may not work for
   * anything else. User be warned.
   */
  inline?: boolean;
  inlineLabelWidth?: string;
  inlineInputWidth?: string;
  disabledClassName?: string;
  hidden?: boolean;
  xout?: (() => void) | null;
  componentLabelRight?: React.ReactNode;
  hintUnderLabel?: boolean;
  noMax?: boolean; // new prop to ignore max length check
  noPadding?: boolean;
  onKeyDownCallback?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const Input = ({
  ref,
  name,
  label,
  value,
  onChange,
  onBlur,
  errors,
  className = '',
  icon,
  disabled = false,
  onFocus,
  type = 'text',
  hint,
  hintOnClick,
  autoComplete,
  inputClassName,
  inputStyle = {},
  placeholder,
  min,
  max,
  step,
  clickIcon,
  autoFocus = false,
  inputMode = 'text',
  pattern,
  loading,
  maxLength,
  iconRight,
  labelClassName = 'block text-sm font-medium leading-5 text-gray-900 dark:text-dark-gray-200',
  macros,
  downError,
  initialValue,
  tooltip,
  tooltipId,
  inputTitle,
  prefix,
  prefixClassName = 'inline-flex items-center px-3 rounded-l-md border border-r border-gray-300 bg-gray-50 text-gray-500 dark:bg-dark-gray-600 dark:border-dark-gray-500 dark:text-dark-gray-300 text-sm',
  showCopyButton = false,
  onCopySuccess,
  inline,
  inlineLabelWidth,
  inlineInputWidth,
  labelTitle,
  hidden = false,
  disabledClassName = 'text-gray-500 dark:text-dark-gray-300 bg-gray-300 dark:bg-dark-gray-500 cursor-not-allowed',
  xout = null,
  componentLabelRight,
  hintUnderLabel = false,
  noMax = false,
  noPadding = false,
  onKeyDownCallback,
}: Props & {
  ref?: any;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => ({
    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    // You can expose other properties or methods as needed
  }));

  const [showPopover, setShowPopover] = useState(false);
  const { filteredMacros } = useMacros(value as string, macros);

  const handleMacroClick = (phrase: string, cursorPlacement?: number) => {
    if (typeof value === 'string' && typeof cursorPlacement === 'number' && inputRef.current) {
      const cutString = value.slice(0, cursorPlacement);
      const lastSlashIndex = cutString.lastIndexOf('/');

      const newStr = `${value.slice(0, lastSlashIndex)}${phrase} ${value.slice(cursorPlacement)}`;

      onChange?.(newStr);
      setShowPopover(false);

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = lastSlashIndex + phrase.length + 1;
          inputRef.current.selectionEnd = lastSlashIndex + phrase.length + 1;
        }
      }, 0);
    }
  };

  const [showDownError, setShowDownError] = React.useState<boolean>(false);
  const [copySuccess, setCopySuccess] = React.useState<boolean>(false);

  const handleCopy = useCallback(() => {
    // Get the full value including prefix if present
    let textToCopy = '';

    if (prefix && typeof prefix === 'string') {
      textToCopy += prefix;
    }

    textToCopy += value?.toString() || '';

    // Copy to clipboard
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopySuccess(true);

        if (onCopySuccess) {
          onCopySuccess();
        }

        // Reset success state after 2 seconds
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  }, [prefix, value, onCopySuccess]);

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    let val: string | number | null = e?.target?.value;
    if (type === 'number' && !noMax && val.toString()?.length > 9) return;

    if (type === 'number') {
      setShowDownError(false);
      val = isEmpty(val) ? null : parseInt(val);

      if (downError && (val || 0) < Number(initialValue)) {
        setShowDownError(true);

        return;
      }
    }

    if (max && val && Number(val) > Number(max)) {
      val = parseInt(max);
    }

    onChange?.(val);
  };

  const onBlurValue = (e: ChangeEvent<HTMLInputElement>) => {
    const targetValue = e?.target?.value;
    onBlur?.(targetValue as string);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, value?: any) => {
    if (onKeyDownCallback) {
      onKeyDownCallback(e);
    }

    if (
      type === 'number' &&
      !/[0-9]/.test(e.key) &&
      typeof value === 'number' &&
      (e.key === 'Backspace' || e.key === 'Delete')
    ) {
      if (value === 0) {
        onChange?.(null);
      } else {
        return;
      }
    }

    if (macros?.length) {
      const key = e.key;
      const code = e.code;
      const isSlash = code === 'Slash' || key === '/';
      const isSpace = code === 'Space' || key === ' ';

      if (isSlash) {
        setShowPopover(true);
      } else if (isSpace) {
        setShowPopover(false);
      }
    } else if (
      type === 'number' &&
      !/[0-9]/.test(e.key) &&
      e.key !== 'Backspace' &&
      e.key !== 'Delete' &&
      e.key !== 'Tab' &&
      e.key !== 'ArrowLeft' &&
      e.key !== 'ArrowRight'
    ) {
      e.preventDefault();
    }
  };

  // useEffect(() => {
  //   const inputElement = inputRef.current;
  //   if (inputElement) {
  //     inputElement.addEventListener('wheel', handleWheel);
  //   }
  //
  //   // Cleanup event listener on component unmount
  //   return () => {
  //     if (inputElement) {
  //       inputElement.removeEventListener('wheel', handleWheel);
  //     }
  //   };
  // }, []); // run only once
  //
  // const handleWheel = (e: WheelEvent) => {
  //   if (document.activeElement === inputRef.current) {
  //     e.preventDefault();
  //     e.stopPropagation();
  //   }
  // };

  if (hidden) return null;

  return (
    <div className={cn(className, inline ? 'flex flex-row gap-0' : 'flex flex-col gap-1')}>
      {label && (
        <div
          className={cn(
            inlineLabelWidth ?? '',
            inline ? 'inline-block' : 'flex',
            'justify-between items-center'
          )}
        >
          <label
            htmlFor={name}
            className={cn(
              labelClassName,
              componentLabelRight ? 'flex flex-row justify-between' : ''
            )}
            title={labelTitle}
          >
            {label}
            {tooltip && <TrivialTooltip text={tooltip} type="info" id={tooltipId} />}
            {componentLabelRight ? componentLabelRight : null}
          </label>
          {showCopyButton && (
            <button
              type="button"
              onClick={handleCopy}
              className="text-gray-400 hover:text-gray-600 dark:text-dark-gray-400 dark:hover:text-dark-gray-200 transition-colors duration-150 focus:outline-none"
              title="Copy to clipboard"
            >
              {copySuccess ? (
                <div className="flex items-center">
                  <span className="text-green-500 text-xs mr-1">Copied!</span>
                  <DocumentDuplicateIcon className="h-4 w-4 text-green-500" />
                </div>
              ) : (
                <DocumentDuplicateIcon className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      )}
      <div className={cn(inlineInputWidth ?? '', inline && 'inline-block')}>
        <div className="relative">
          <div className="relative flex items-stretch grow focus-within:z-10">
            {prefix && <span className={prefixClassName}>{prefix}</span>}
            {icon && (
              <div
                className={[
                  `absolute inset-y-0 ${
                    iconRight ? 'right-4' : 'left-0'
                  } pl-3 flex items-center z-20`,
                  clickIcon ? '' : 'pointer-events-none',
                ].join(' ')}
              >
                {icon}
              </div>
            )}
            <input
              id={name}
              name={name}
              title={inputTitle}
              data-slot="input"
              className={cn(
                'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-primary-500 focus-visible:ring-primary-500',
                prefix ? 'rounded-r-md rounded-l-none border-l-0' : '',
                errors
                  ? 'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-destructive text-destructive placeholder:text-destructive/50'
                  : '',
                disabled ? disabledClassName : '',
                icon && !iconRight ? (typeof icon === 'string' ? 'pl-5' : 'pl-10') : '',
                inputClassName
              )}
              value={value ?? ''}
              data-cy={placeholder}
              onChange={onChangeValue}
              onBlur={onBlurValue}
              onFocus={onFocus}
              type={type}
              disabled={disabled}
              ref={inputRef}
              autoComplete={autoComplete}
              placeholder={placeholder}
              min={min}
              max={max}
              step={step}
              autoFocus={autoFocus}
              inputMode={inputMode ? inputMode : type === 'number' ? 'numeric' : 'text'}
              pattern={pattern ? pattern : type === 'number' ? '[0-9.]+' : undefined}
              style={inputStyle}
              maxLength={maxLength}
              onKeyDown={e => onKeyDown(e, value)}
            />
            {!!hint && hintUnderLabel && (
              <p
                className={[
                  'text-sm',
                  hintOnClick && !disabled
                    ? 'text-primary-600 font-semibold hover:text-primary-500 cursor-pointer italic'
                    : 'text-gray-500 italic',
                ].join(' ')}
                id={`${name}-hint`}
                onClick={hintOnClick}
              >
                {hint}
              </p>
            )}
            {value && xout ? (
              <div
                className="absolute top-3 right-2 w-4 h-4 text-gray-500 cursor-pointer"
                onClick={e => {
                  if (e && e.stopPropagation) e.stopPropagation();
                  xout?.();
                }}
              >
                <XMarkIcon />
              </div>
            ) : null}
            {errors && <ErrorIcon />}
            {loading && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Loading color="text-gray-400" size={6} />
              </div>
            )}
          </div>
        </div>
        <FieldErrors errors={errors} />
        {showDownError && !!downError && (
          <FieldErrors errors={{ message: downError }} className="text-xs leading-4" />
        )}
        {!!hint && !hintUnderLabel && (
          <p
            className={[
              'text-sm',
              hintOnClick && !disabled
                ? 'text-primary-600 font-semibold hover:text-primary-500 cursor-pointer'
                : 'text-gray-500',
            ].join(' ')}
            id={`${name}-hint`}
            onClick={hintOnClick}
          >
            {hint}
          </p>
        )}
        {!!(showPopover && macros?.length) && (
          <MacrosPopover
            showPopover={showPopover}
            setShowPopover={setShowPopover}
            filteredMacros={filteredMacros}
            handleMacroClick={handleMacroClick}
            cursorPosition={inputRef.current?.selectionStart ?? 0}
          />
        )}
      </div>
    </div>
  );
};
